import { Typography } from '@equinor/eds-core-react'
import {
    AboutPageContainer,
    TextContainer,
    ImageContainer,
    StyledLink,
    TextAndImageContainer,
    ColumnsWrapper,
    TitleContainer,
} from '@/components/styles/AboutStyles'

function HowToGetStarted() {
    return (
        <>
            <AboutPageContainer>
                <TitleContainer>
                    <Typography variant="h1">How to use IIoT@Equinor</Typography>
                </TitleContainer>
                <ColumnsWrapper>
                    <TextAndImageContainer>
                        <TextContainer>
                            <Typography variant="body_short">
                                The IIoT application serves as a portal for locating, purchasing, installing, and
                                managing wireless sensors. Here you get an overview of how to get started with IIoT
                                sensors and how to use the IIoT portal.
                            </Typography>
                        </TextContainer>
                        <ImageContainer>
                            <img src="how-to-illustration-2.png" />
                        </ImageContainer>
                        <TextContainer>
                            <Typography variant="h3">How to order a sensor</Typography>
                            <Typography variant="body_short">
                                Navigate to the <StyledLink href={'/portfolio'}>Portfolio page</StyledLink>, select the
                                sensor that matches your use case, and click "Order" (for now, please contact the IIoT
                                team or procurement). Complete the required details for your order.
                            </Typography>
                            <br />
                            <Typography variant="h3">How to obtain plant access for sensor setup</Typography>
                            <Typography variant="body_short">
                                To set up a sensor, you must first be granted access for your plant. Visit{' '}
                                <StyledLink
                                    href={'https://accessit.equinor.com/Search/Search?term=iiot'}
                                    target="_blank"
                                >
                                    AccessIT
                                </StyledLink>
                                , and submit a request for <strong>"IIoT Application - [your plant]."</strong>
                            </Typography>
                        </TextContainer>
                    </TextAndImageContainer>
                    <TextAndImageContainer>
                        <TextContainer>
                            <Typography variant="h3">How to set up your sensor</Typography>
                            <Typography variant="body_short">
                                After you have received the sensor and have access in AccessIT, you can start the setup.
                                <br />
                                <br />
                                Go to the <StyledLink href={'/dashboard'}>Sensor overview</StyledLink> and click
                                <strong> "Add new sensor"</strong>.
                                <br />
                                <br />
                                You will need to fill in the following fields to be able to press{' '}
                                <strong>"Create" </strong>
                                and finalize the setup:
                            </Typography>
                            <ul>
                                <li>Plant</li>
                                <li>Project</li>
                                <li>Device</li>
                                <li>Vendor serial number, which can be scanned from the QR code on the sensor</li>
                                <li>Superior tag</li>
                                <li>Coordinates (Echo)</li>
                            </ul>
                            <Typography variant="body_short">
                                If you want to save your progress and finish later, or let someone else complete the
                                setup, you only need to fill in plant, project, device and vendor serial number and
                                press <strong>"Save as draft"</strong>.
                                <br />
                                <br />
                                To add more sensors for the same plant, project, and device, tap{' '}
                                <strong>"Add another sensor" </strong>
                                after pressing “Create” or “Save as draft”. These fields will be pre-filled for you, so
                                you only need to complete the remaining fields.
                            </Typography>
                            <br />
                            <Typography variant="h3">How to activate sensors</Typography>
                            <Typography variant="body_short">
                                After you finish setting up a new sensor, it needs to be activated before it can start
                                working. Activation can be as simple as pressing a button, swiping a magnet or other
                                procedures depending on the model in question. Please check the manual for your specific
                                sensor on the <StyledLink href={'/portfolio'}>Portfolio page</StyledLink> to see exactly
                                how to activate it.
                            </Typography>
                            <br />
                            <Typography variant="h3">How to manage active sensors</Typography>
                            <Typography variant="body_short">
                                In the <StyledLink href={'/dashboard'}>Sensor overview</StyledLink> you can view all
                                installed IIoT sensors. By selecting a Plant in your user settings, the Sensor overview
                                will automatically filter to display only the sensors relevant to you. You can modify
                                both the filter and your user settings at any time. Additionally, the Sensor overview
                                includes sorting functionality, allowing you to organize the sensors based on parameters
                                that are important to you.
                                <br />
                                <br />
                                You can also scan a sensor mounted at the facility to retrieve information about it. To
                                do this, navigate to the Sensor overview and click "Scan sensor." By scanning the QR
                                code, the sensor associated with the corresponding vendor serial number will be
                                displayed. Click "Details" to access more information about the sensor.
                                <br />
                                <br />A (temporary) sensor may be moved. To do this, go to “Sensor overview,” press edit
                                on the sensor, and change the main tag and/or coordinates. You will then be asked to
                                confirm the move. If you confirm, a new tag name will be created, and new data will be
                                stored separately.
                            </Typography>
                            <br />
                            <Typography variant="h3">Decommissioning</Typography>
                            <Typography variant="body_short">
                                When you remove a sensor, the IIoT application will clear all digital traces and set its
                                tag status to “HISTORIC” in STID. The physical device can stay in place until field
                                personnel are ready to remove it. Measurement data will not be deleted.
                            </Typography>
                        </TextContainer>
                    </TextAndImageContainer>
                </ColumnsWrapper>
            </AboutPageContainer>
        </>
    )
}

export default HowToGetStarted
